import { FC, useMemo } from "react";

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import { useAxesTooltip } from "@mui/x-charts/ChartsTooltip";

import { useTranslate } from "../../../common/hooks/useTranslate";

import { ChartLegendItem } from "./ChartLegendItem";
import { formatTooltipAxisValue, TooltipAxisValueFormatter } from "./formatTooltipAxisValue";

import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

export type ChartSeriesTooltipProps = {
  xAxisValueFormatter?: TooltipAxisValueFormatter;
  yAxisValueFormatter?: (value: number | null) => string;
  excludeSeriesIds?: string[];
  showTotal?: boolean;
  totalLabel?: string;
  totalValueFormatter?: (value: number) => string;
};

export const ChartSeriesTooltip: FC<ChartSeriesTooltipProps> = ({
  xAxisValueFormatter,
  yAxisValueFormatter,
  excludeSeriesIds,
  showTotal,
  totalLabel,
  totalValueFormatter,
}) => {
  const theme = useTheme();
  const axesTooltipData = useAxesTooltip();
  const tooltipData = axesTooltipData?.[0];
  const { t } = useTranslate();

  const formattedXAxisValue = useMemo(
    () => (tooltipData ? formatTooltipAxisValue(tooltipData, xAxisValueFormatter) : ""),
    [tooltipData, xAxisValueFormatter]
  );

  const visibleSeriesItems = useMemo(() => {
    if (!tooltipData) {
      return [];
    }
    return [...tooltipData.seriesItems]
      .reverse()
      .filter((series) => !excludeSeriesIds?.includes(series.seriesId.toString()));
  }, [tooltipData, excludeSeriesIds]);

  const formattedTotal = useMemo(() => {
    if (!showTotal) {
      return null;
    }
    const total = visibleSeriesItems.reduce((sum, series) => sum + ((series.value as number) ?? 0), 0);
    return totalValueFormatter?.(total) ?? yAxisValueFormatter?.(total) ?? total.toString();
  }, [showTotal, visibleSeriesItems, yAxisValueFormatter, totalValueFormatter]);

  if (!tooltipData) {
    return null;
  }

  return (
    <Paper
      sx={{
        minWidth: 225,
        maxWidth: 350,
        padding: theme.spacing(2, 2.5),
      }}
    >
      <Stack gap={1.5}>
        <DashboardTypography variant="subtitle2" vibe="text-neutral-moderate">
          {formattedXAxisValue}
        </DashboardTypography>
        <DashboardDivider />
        <Stack alignItems="flex-start" gap={1}>
          {visibleSeriesItems.map((series) => (
            <ChartLegendItem
              key={series.seriesId}
              label={series.formattedLabel}
              color={series.color}
              value={yAxisValueFormatter?.(series.value as number | null) ?? series.formattedValue}
            />
          ))}
        </Stack>
        {showTotal && (
          <>
            <DashboardDivider />
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
              <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                {totalLabel ?? t("common", "chart.total")}
              </DashboardTypography>
              <DashboardTypography variant="subtitle2" vibe="text-neutral-intense">
                {formattedTotal}
              </DashboardTypography>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
};
